/**
 * The office hours and the Book-a-call and walkthrough links, for the signed-in coach. Admins
 * set them at /admin/calls-and-links (`~/lib/functions/admin-settings`).
 *
 * Aliases: calls and links api, live calls api, office hours read
 */

import { createServerFn } from "@tanstack/react-start";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import type { AdminSettingValue } from "~/lib/shared/admin-settings";

export interface CoachCallsAndLinks {
  officeHours: AdminSettingValue<"office_hours">;
  bookACallUrl: AdminSettingValue<"book_a_call_url">;
  marketingSuiteWalkthroughUrl: AdminSettingValue<"marketing_suite_walkthrough_url">;
}

/**
 * The admin settings every coach may read. Each one is named here, so a
 * setting added later reaches coaches only by being added to this list.
 *
 * The Accelerator group call is left out: its join link is for coaches in the
 * Accelerator only, so it is read through `getMyAccelerator`
 * (`~/lib/functions/accelerator`), which checks her coach row.
 */
export const getCoachCallsAndLinksFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async (): Promise<CoachCallsAndLinks> => {
    const { getAdminSettings } = await import("~/lib/server/admin-settings");
    const settings = await getAdminSettings();
    return {
      officeHours: settings.office_hours.value,
      bookACallUrl: settings.book_a_call_url.value,
      marketingSuiteWalkthroughUrl:
        settings.marketing_suite_walkthrough_url.value,
    };
  });
